import React from 'react';
import { Link } from 'react-router-dom';
import './TherapeuticCare.css';

function TherapeuticCare() {
  return (
    <div className="therapeutic-care-page">
      {/* Header/Navigation */}
      <header className="header">
        <nav className="navbar">
          <div className="logo">
            <Link to="/"><h1>Malach <span>Healthcare</span></h1></Link>
          </div>
          <ul className="nav-links">
            <li><Link to="/">Home</Link></li>
            <li><Link to="/#about">About Us</Link></li>
            <li><Link to="/#services">Services</Link></li>
            <li><Link to="/#contact">Contact</Link></li>
          </ul>
          <Link to="/#contact" className="btn btn-primary">Book Consultation</Link>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="service-hero therapeutic-hero">
        <div className="service-hero-content">
          <h1>Therapeutic Care for Families Who Are Hurting</h1>
          <p>Healing, growth, and renewed hope—one step at a time</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="service-content">
        <div className="service-intro">
          <p className="intro-highlight">
            At Malach Healthcare, we understand that reaching out for therapeutic support is not 
            an easy step. Many families come to us feeling overwhelmed, exhausted, confused, 
            grieved, or unsure of where to turn next. You may be carrying concerns about your 
            child, your family's well-being, or your own emotional health—and you may be wondering 
            if anyone truly understands what you're going through.
          </p>
          <p className="reassurance">
            You are not alone. And you do not have to navigate this journey by yourself.
          </p>
        </div>

        <div className="why-therapy">
          <h2>Why Families Seek Therapy</h2>
          <p>
            Families often seek therapy because something feels out of balance—behavioral challenges, 
            emotional distress, trauma, anxiety, communication struggles, or life transitions that 
            feel too heavy to manage alone.
          </p>
          <div className="partnership-note">
            <p>
              At Malach Healthcare, in collaboration with <strong>Valor Counseling and Consultation 
              Services, LLC</strong>, we don't believe in labels or quick fixes. We believe in 
              listening first.
            </p>
          </div>
        </div>

        <div className="approach-section">
          <h2>Our Approach to Healing</h2>
          <div className="approach-content">
            <div className="approach-icon-large">💜</div>
            <p>
              Our therapists take time to understand your family's unique story, strengths, and 
              challenges. We work to create a safe, respectful space where every voice matters 
              and healing can begin at a pace that feels right for you.
            </p>
          </div>
        </div>

        <div className="meet-you-section">
          <h2>Support That Meets You Where You Are</h2>
          <p>
            We know that families have different needs, schedules, cultures, and comfort levels 
            with therapy. Our services are designed to be flexible, compassionate, and individualized. 
            Whether you are seeking support for a child, a teen, a parent, or the entire family, 
            our goal is to provide care that feels supportive—not overwhelming.
          </p>
          <p className="encouragement">
            You don't need to have all the answers before starting. You just need a place that 
            will walk with you as you find them.
          </p>
        </div>

        <div className="priorities-section">
          <h2>What We Prioritize</h2>
          <p className="priorities-intro">
            Choosing a therapeutic provider is a deeply personal decision. We prioritize:
          </p>
          <div className="priorities-grid">
            <div className="priority-item">
              <div className="priority-icon">🔒</div>
              <h3>Emotional Safety</h3>
              <p>Emotional safety and confidentiality</p>
            </div>
            <div className="priority-item">
              <div className="priority-icon">🤝</div>
              <h3>Respect</h3>
              <p>Respect for your family's values and experiences</p>
            </div>
            <div className="priority-item">
              <div className="priority-icon">📚</div>
              <h3>Evidence-Based</h3>
              <p>Evidence-based, ethical therapeutic practices</p>
            </div>
            <div className="priority-item">
              <div className="priority-icon">🌿</div>
              <h3>Welcoming Space</h3>
              <p>A calm, welcoming environment</p>
            </div>
          </div>
          <p className="belief-statement">
            We believe that healing happens best when families feel understood, supported, 
            and empowered—not judged.
          </p>
        </div>

        <div className="first-step">
          <h2>Taking the First Step</h2>
          <p>
            Taking the first step can be the hardest—but if you're reading this page, you may 
            already be carrying the weight of deciding what's best for your family. Let us help 
            lighten that load.
          </p>
          <p className="empowerment">
            Reaching out does not mean you've failed—it means you care deeply and are willing 
            to seek support.
          </p>
        </div>

        <div className="closing-message">
          <p>
            When you're ready, Malach Healthcare is here to listen, to guide, and to support 
            your family toward healing, growth, and renewed hope.
          </p>
          <div className="closing-affirmations">
            <p>You deserve care.</p>
            <p>Your family deserves support.</p>
            <p className="final-hope">And healing is possible.</p>
          </div>
          <Link to="/#contact" className="btn btn-primary btn-large">Book A Free Consultation</Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-section">
            <h3>Malach <span>Healthcare</span></h3>
            <p>Empowering golden years with compassionate care.</p>
            <div className="social-links">
              <a href="#facebook">📘</a>
              <a href="#twitter">🐦</a>
              <a href="#instagram">📷</a>
              <a href="#linkedin">💼</a>
            </div>
          </div>
          <div className="footer-section">
            <h4>Quick Links</h4>
            <ul>
              <li><Link to="/">Home</Link></li>
              <li><Link to="/#about">About Us</Link></li>
              <li><Link to="/#services">Services</Link></li>
              <li><Link to="/#contact">Contact</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Our Services</h4>
            <ul>
              <li><Link to="/personal-care">Personal Care</Link></li>
              <li><Link to="/companion-care">Companion Care</Link></li>
              <li><Link to="/dementia-care">Dementia Care</Link></li>
              <li><Link to="/therapeutic-care">Therapeutic Care</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Contact Info</h4>
            <p>📍 740 Main St<br />Waltham, MA 02451</p>
            <p>📞 (781) 697-6330</p>
            <p>✉️ info@malachhealthcare.com</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>Copyright © 2025 | All Rights Reserved | Malach Healthcare</p>
        </div>
      </footer>
    </div>
  );
}

export default TherapeuticCare;
